import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import AppHeader from './components/AppHeader';
import MobileBottomNav from './components/MobileBottomNav';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Products from './pages/Products';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupPending from './pages/SignupPending';
import GoogleCallback from './pages/GoogleCallback';
import AdminDashboard from './pages/AdminDashboard';
import WelcomeEmail from './pages/WelcomeEmail';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import EditProfile from './pages/EditProfile';
import Genealogy from './pages/Genealogy';
import Invite from './pages/Invite';
import PlaceUser from './pages/PlaceUser';
import PlaceMyReferrals from './pages/PlaceMyReferrals';
import ManageUplines from './pages/ManageUplines';
import PackagePurchase from './pages/PackagePurchase';
import Wallet from './pages/Wallet';
import SetWellisPassword from './pages/SetWellisPassword';
import ForceResetPassword from './pages/ForceResetPassword';
import MarketingPlan from './pages/MarketingPlan';
import Ranking from './pages/Ranking';
import DirectReferral from './pages/DirectReferral';
import TeamBonus from './pages/TeamBonus';
import TeamBonusOld from './pages/TeamBonusOld';
import Archived from './pages/Archived';
import AuditLogs from './pages/AuditLogs';
import AdminGuide from './pages/AdminGuide';
import AdminCreditTopups from './pages/AdminCreditTopups';
import Guides from './pages/Guides';
import Monthly from './pages/Monthly';
import MemberRewards from './pages/MemberRewards';
import AdminStats from './pages/AdminStats';
import SalesTeam from './pages/SalesTeam';
import SalesReports from './pages/SalesReports';
import Reports from './pages/Reports';
import RejectedSales from './pages/RejectedSales';
import Balances from './pages/Balances';
import SignupInternal from './pages/SignupInternal';
import SignupWizard from './pages/SignupWizard';
import RnrCreditRequests from './pages/RnrCreditRequests';
import DevDashboard from './pages/DevDashboard';

// Placeholder pages (to be implemented)
function MembersPage() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">👥 Members</h1>
        <p className="text-on-background/60">Coming soon</p>
      </div>
    </div>
  );
}

function SettingsPage() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">⚙️ Settings</h1>
        <p className="text-on-background/60">Coming soon</p>
      </div>
    </div>
  );
}

function InactivityNotifications() {
  const { inactivityWarning, inactivityNotification, extendSession } = useAuth();

  return (
    <>
      {/* Warning: About to expire (shown 30 seconds before logout) */}
      {inactivityWarning && (
        <div className="fixed top-4 right-4 bg-yellow-500 text-black px-6 py-4 rounded-lg shadow-lg z-50 animate-bounce">
          <div className="flex items-center gap-3">
            <span>⏱️</span>
            <div className="flex-1">
              <p className="font-semibold">Session Expiring Soon</p>
              <p className="text-sm">You'll be logged out in 30 seconds due to inactivity.</p>
            </div>
            <button
              onClick={extendSession}
              className="ml-4 bg-black text-yellow-500 px-4 py-2 rounded font-medium hover:bg-gray-900 transition-colors"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}

      {/* Notification: Already expired */}
      {inactivityNotification && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-pulse">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <div>
              <p className="font-semibold">Session Expired</p>
              <p className="text-sm">You were logged out due to inactivity. Please log in again.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <InactivityNotifications />
        <AppHeader />
        <Navigation />
        <MobileBottomNav />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignupWizard mode="public" />} />
            <Route path="/signup-pending" element={<SignupPending />} />
            <Route path="/set-wellis-password" element={<SetWellisPassword />} />
            <Route path="/force-reset-password" element={<ForceResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/packages" element={<PackagePurchase />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/welcome-email" element={<WelcomeEmail />} />
            <Route path="/admin/manage-uplines" element={<ManageUplines />} />
            <Route path="/admin/place-user" element={<PlaceUser />} />
            <Route path="/place-referrals" element={<PlaceMyReferrals />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/genealogy" element={<Genealogy />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/marketing-plan" element={<MarketingPlan />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/archived/direct-referral" element={<DirectReferral />} />
            <Route path="/archived/team-bonus-v2" element={<TeamBonus />} />
            <Route path="/archived" element={<Archived />} />
            <Route path="/archived/team-bonus" element={<TeamBonusOld />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
            <Route path="/admin-guide" element={<AdminGuide />} />
            <Route path="/admin-credit-topups" element={<AdminCreditTopups />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/archived/passive" element={<Monthly />} />
            <Route path="/member-rewards" element={<MemberRewards />} />
            <Route path="/admin-dashboard" element={<AdminStats />} />
            <Route path="/sales-team" element={<SalesTeam />} />
            <Route path="/sales-reports" element={<SalesReports />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/rejected-sales" element={<RejectedSales />} />
            <Route path="/balances" element={<Balances />} />
            <Route path="/signup-internal" element={<SignupWizard mode="internal" />} />
            <Route path="/signup-wizard" element={<SignupWizard mode="internal" />} />
            <Route path="/archived/signup" element={<Signup />} />
            <Route path="/archived/signup-internal" element={<SignupInternal />} />
            <Route path="/rnr-credit-requests" element={<RnrCreditRequests />} />
            <Route path="/dev-dashboard" element={<DevDashboard />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
        <Footer />
      </Router>
    </AuthProvider>
  );
}
